import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { KeyboardArrowDown, KeyboardArrowUp, Sync } from '@material-ui/icons';
import { unpackToDateTime } from '@campfire/hot-date';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { GET_MASTER_CHANGES } from './enterprise-master-changes.gql';
import { entityTypeHeading, entityTypeIcon } from './enterprise-entity-labels';
import { SyncChangeList } from './SyncChangeList';
import { EnterpriseSettingsPush } from './EnterpriseSettingsPush';
import { GetMasterChanges } from './__generated__/GetMasterChanges';
import { PUSH_WITHOUT_REVIEW_DEFAULT_KEY } from './EnterprisePushDefault';
import { GET_CONFIG_ITEM } from '../admin-console-activity-reporting/config-item.gql';
import { GetConfigItem } from '../admin-console-activity-reporting/__generated__/GetConfigItem';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    heading: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    containerPanel: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    headingPanel: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    bodyPanel: {
      fontSize: '14px',
      color: '#808080',
    },
    tableHead: {
      '& th': {
        fontWeight: 600,
        fontSize: '11px',
        textTransform: 'uppercase' as const,
        color: theme.color.grey.neutral200,
        borderBottom: '1px solid #e0e0e0',
      },
    },
    entityName: {
      fontWeight: 600,
      fontSize: '14px',
      color: theme.color.grey.neutralBrand900,
    },
    cellText: {
      fontSize: '13px',
      color: theme.color.grey.neutral400,
    },
    mutedCellText: {
      fontSize: '13px',
      color: theme.color.grey.neutral200,
    },
    typeIcon: {
      fontSize: '16px',
      color: theme.color.grey.neutral200,
      marginRight: '6px',
    },
    pushButton: {
      borderRadius: 4,
      textTransform: 'none' as const,
      fontSize: '13px',
      whiteSpace: 'nowrap' as const,
    },
    forceLabel: {
      fontSize: '13px',
      color: theme.color.grey.neutral400,
    },
    detailCell: {
      paddingTop: 0,
      paddingBottom: 0,
      borderBottom: 'none',
    },
    detailPanel: {
      backgroundColor: theme.color.grey.background,
      borderRadius: '4px',
      padding: '4px 16px',
      margin: '8px 0 16px 0',
    },
  })
);

export const AdminConsolePushUpdates = () => {
  const classes = useStyles();
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });

  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [expandedIds, setExpandedIds] = React.useState<string[]>([]);
  const [pushing, setPushing] = React.useState(false);
  const [forceMode, setForceMode] = React.useState(false);
  const forceModeTouched = React.useRef(false);

  const { data: pushDefaultData } = useCampfireQuery<GetConfigItem, { key: string }>(GET_CONFIG_ITEM, {
    options: { variables: { key: PUSH_WITHOUT_REVIEW_DEFAULT_KEY } },
  });

  React.useEffect(() => {
    const item = pushDefaultData?.vm.configItem;
    if (!item || forceModeTouched.current) return;
    setForceMode(item.configItemValue?.flagValue ?? item.defaultValue);
  }, [pushDefaultData]);

  const { data, loading, refetch } = useCampfireQuery<GetMasterChanges, {}>(GET_MASTER_CHANGES);

  const changes = data?.vm.enterpriseMasterChanges ?? [];
  const allSelected = changes.length > 0 && selectedIds.length === changes.length;

  const toggle = (entityId: string) =>
    setSelectedIds((current) =>
      current.includes(entityId) ? current.filter((id) => id !== entityId) : [...current, entityId]
    );

  const toggleAll = () => setSelectedIds(allSelected ? [] : changes.map((change) => change.entityId));

  const toggleExpanded = (entityId: string) =>
    setExpandedIds((current) =>
      current.includes(entityId) ? current.filter((id) => id !== entityId) : [...current, entityId]
    );

  const handlePush = () => {
    setPushing(true);

    const entities = changes
      .filter((change) => selectedIds.includes(change.entityId))
      .map((change) => ({
        entityType: change.entityType,
        entityId: change.entityId,
        pushMode: forceMode ? 'force' : 'suggest',
      }));

    campfireMutation
      .run({ url: `${apiUrl}/vm/enterprise/push-updates`, method: 'post', data: { entities } })
      .then((res: any) => {
        if (!res.ok) {
          setPushing(false);
          setSnackbar({ variant: 'error', open: true, message: res?.data?.message || 'Could not push the updates' });
          return;
        }

        const result = res?.data?.data ?? {};
        const subAccountLabel = `${result.subAccounts ?? 0} sub-account${result.subAccounts === 1 ? '' : 's'}`;
        const changeLabel = `${result.changes ?? 0} change${result.changes === 1 ? '' : 's'}`;

        setSnackbar({
          variant: 'success',
          open: true,
          message: forceMode
            ? `${result.applied ?? 0} of ${changeLabel} applied to ${subAccountLabel}`
            : `${changeLabel} sent to ${subAccountLabel}`,
        });
        setSelectedIds([]);
        setPushing(false);

        if (refetch) {
          setTimeout(refetch, 0);
        }
      })
      .catch(() => {
        setPushing(false);
        setSnackbar({ variant: 'error', open: true, message: 'Could not push the updates' });
      });
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Box>
          <Typography variant='h2' className={classes.headingTitle}>
            Push Updates
          </Typography>
          <Typography variant='body2'>{'Send changes you have made to the sub-accounts that hold them'}</Typography>
        </Box>
        <Box display='flex' alignItems='center'>
          <FormControlLabel
            control={
              <Switch
                color='primary'
                checked={forceMode}
                onChange={(event) => {
                  forceModeTouched.current = true;
                  setForceMode(event.target.checked);
                }}
              />
            }
            label={<Typography className={classes.forceLabel}>Apply without review</Typography>}
          />
          <Button
            variant='contained'
            color='primary'
            disableElevation
            startIcon={<Sync />}
            className={classes.pushButton}
            onClick={handlePush}
            disabled={pushing || selectedIds.length === 0}
          >
            {pushing ? 'Pushing…' : `Push ${selectedIds.length > 0 ? selectedIds.length : ''}`.trim()}
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress style={{ marginTop: '1rem' }} />}

      {!loading && changes.length === 0 ? (
        <Box className={classes.containerPanel}>
          <Typography className={classes.headingPanel}>
            <Sync style={{ fontSize: '14px', paddingRight: '5px' }} /> Nothing to push
          </Typography>
          <Typography variant='body2' className={classes.bodyPanel}>
            {
              'Nothing has changed since these entities were last shared. Edit something a sub-account holds and it will appear here.'
            }
          </Typography>
        </Box>
      ) : (
        !loading && (
          <Box className={classes.containerPanel}>
            <Typography className={classes.headingPanel}>
              <Sync style={{ fontSize: '14px', paddingRight: '5px' }} /> Changed since last shared
            </Typography>
            <Typography variant='body2' className={classes.bodyPanel}>
              {`${changes.length} entit${changes.length === 1 ? 'y has' : 'ies have'} changed. ` +
                'Sub-account admins review what you send and choose whether to accept it.'}
            </Typography>

            <TableContainer style={{ marginTop: '1rem' }}>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell padding='checkbox'>
                      <Checkbox color='primary' checked={allSelected} onChange={toggleAll} />
                    </TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Changes</TableCell>
                    <TableCell align='right'>Last Shared</TableCell>
                    <TableCell padding='checkbox' />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changes.map((change) => {
                    const TypeIcon = entityTypeIcon(change.entityType);
                    const expanded = expandedIds.includes(change.entityId);

                    return (
                      <React.Fragment key={change.entityId}>
                        <TableRow hover style={{ cursor: 'pointer' }} onClick={() => toggle(change.entityId)}>
                          <TableCell padding='checkbox'>
                            <Checkbox color='primary' checked={selectedIds.includes(change.entityId)} />
                          </TableCell>
                          <TableCell>
                            <Typography className={classes.entityName}>{change.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box display='flex' alignItems='center'>
                              <TypeIcon className={classes.typeIcon} />
                              <Typography className={classes.cellText}>
                                {entityTypeHeading(change.entityType)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography className={classes.cellText}>{change.changeSummary}</Typography>
                          </TableCell>
                          <TableCell align='right'>
                            <Typography className={classes.mutedCellText}>
                              {unpackToDateTime(change.lastSharedAt).toFormat('d MMM y')}
                            </Typography>
                          </TableCell>
                          <TableCell padding='checkbox'>
                            <IconButton
                              size='small'
                              aria-label={expanded ? 'Hide changes' : 'Show changes'}
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleExpanded(change.entityId);
                              }}
                            >
                              {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.detailCell} colSpan={6}>
                            <Collapse in={expanded} timeout='auto' unmountOnExit>
                              <Box className={classes.detailPanel}>
                                <SyncChangeList changes={change.changes} />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )
      )}

      <EnterpriseSettingsPush
        className={classes.containerPanel}
        headingClassName={classes.headingPanel}
        bodyClassName={classes.bodyPanel}
      />
    </Box>
  );
};
